/**
 * Legacy Token Storage Utility
 * 
 * This utility provides access to persisted Redux state for backward compatibility.
 * Now that we use Redux as single source of truth, this mainly serves as a bridge
 * for existing code that might still reference tokenStorage.
 */

export interface TokenData {
  accessToken: string | null;
  refreshToken: string | null;
  expiresIn: number | null;
}

class LegacyTokenStorage {
  getTokens(): TokenData {
    return {
      accessToken: this.getAccessToken(),
      refreshToken: this.getRefreshToken(),
      expiresIn: this.getExpiresIn(),
    };
  }

  getRefreshToken(): string | null {
    try {
      // Get refresh token from Redux persist localStorage
      const persistedState = localStorage.getItem('persist:auth');
      if (persistedState) {
        const authData = JSON.parse(persistedState);
        if (authData.refreshToken) {
          return JSON.parse(authData.refreshToken);
        }
      }
    } catch (error) {
      console.warn('🔧 [TOKEN STORAGE] Failed to get refresh token:', error);
    }
    return null;
  }

  getAccessToken(): string | null {
    try {
      // Get access token from Redux persist localStorage
      const persistedState = localStorage.getItem('persist:auth');
      if (persistedState) {
        const authData = JSON.parse(persistedState);
        if (authData.accessToken) {
          return JSON.parse(authData.accessToken);
        }
      }
    } catch (error) {
      console.warn('🔧 [TOKEN STORAGE] Failed to get access token:', error);
    }
    return null;
  }

  private getExpiresIn(): number | null {
    try {
      // Get expiresIn from Redux persist localStorage
      const persistedState = localStorage.getItem('persist:auth');
      if (persistedState) {
        const authData = JSON.parse(persistedState);
        if (authData.expiresIn) {
          return JSON.parse(authData.expiresIn);
        }
      }
    } catch (error) {
      console.warn('🔧 [TOKEN STORAGE] Failed to get expiresIn:', error);
    }
    return null;
  }

  // Legacy methods for backward compatibility with tests - no-op implementations
  setTokens(tokens: TokenData): void {
    console.warn('🔧 [TOKEN STORAGE] setTokens called on legacy storage - no-op (use Redux state)');
  }

  clearTokens(): void {
    console.warn('🔧 [TOKEN STORAGE] clearTokens called on legacy storage - no-op (use Redux state)');
  }

  updateFromReduxState(tokens: TokenData): void {
    console.warn('🔧 [TOKEN STORAGE] updateFromReduxState called on legacy storage - no-op (use Redux state)');
  }

  onTokenUpdate(callback: (tokens: TokenData) => void): () => void {
    console.warn('🔧 [TOKEN STORAGE] onTokenUpdate called on legacy storage - no-op (use Redux state)');
    return () => {}; // Return no-op cleanup function
  }

  syncToReduxState(): TokenData {
    console.warn('🔧 [TOKEN STORAGE] syncToReduxState called on legacy storage - returning current tokens');
    return this.getTokens();
  }
}

// Singleton instance for backward compatibility
export const tokenStorage = new LegacyTokenStorage();

// Legacy hook export for backward compatibility - returns no-op functions
export const useTokenStorage = () => {
  console.warn('🔧 [TOKEN STORAGE] useTokenStorage called on legacy storage - no-op (use Redux state)');
  return {
    getTokens: () => tokenStorage.getTokens(),
    setTokens: () => {},
    clearTokens: () => {},
    onTokenUpdate: () => () => {},
  };
};